﻿<script setup lang="ts">
    import { $Resources } from '@cwd/lib/main';
    import { inject, onMounted, reactive } from 'vue';
    import { getAll, createDomainWhitelist, deleteDomainWhitelist, DomainWhitelist } from './DomainWhitelists.access';
    import Modal from '@shared/components/Modal.vue';
    import DeleteButton from '@shared/components/DeleteButton.vue';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import { bsAlert } from '@shared/components/BsAlerts';
    import Loading from '@shared/components/Loading.vue';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { Permissions } from '@shared/Root/Permissions';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);

    const state = reactive<{
        domainWhitelists: DomainWhitelist[] | null;
        showCreateModal: boolean;
        isLoading: boolean;
        domain: string;
    }>({
        domainWhitelists: null,
        showCreateModal: false,
        isLoading: false,
        domain: '',
    });

    onMounted(async () => {
        state.isLoading = true;

        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Account',
            Permission: Permissions.Write,
        };
        var hasScope = scopeAccess.hasScope(requireScopeParams);

        if (hasScope) {
            await get();
        }
    });

    async function get() {
        await getAll()
            .then((domainWhitelists) => {
                state.domainWhitelists = domainWhitelists;
            })
            .finally(() => (state.isLoading = false));
    }

    async function create(): Promise<void> {
        state.isLoading = true;

        let domainWhitelist: DomainWhitelist = {
            Domain: state.domain,
        };

        await createDomainWhitelist(domainWhitelist)
            .then(async () => {
                state.showCreateModal = false;

                state.domain = '';

                await get();
            })
            .catch(() => {
                bsAlert({ title: $Resources.DomainWhitelistsErrorHeading, content: $Resources.DomainWhitelistsErrorBody, type: 'danger' });
            })
            .finally(() => (state.isLoading = false));
    }

    async function removeDomainWhitelist(domainWhitelist: DomainWhitelist): Promise<void> {
        state.isLoading = true;
        await deleteDomainWhitelist(domainWhitelist)
            .then(async () => {
                await get();
            })
            .finally(() => (state.isLoading = false));
    }

    function showCreateModal(): void {
        state.showCreateModal = true;
    }
</script>

<template>
    <h2 class="main-sub-title">{{ $Resources.DomainWhitelistsHeader }}</h2>

    <p class="m-lg-b">
        {{ $Resources.DomainWhitelistsDescription }}
    </p>

    <div class="form-group">
        <button type="button" class="btn btn-primary" @click="showCreateModal" :disabled="state.isLoading">
            {{ $Resources.CreateDomainWhitelist }}
        </button>
    </div>

    <Loading :show="state.isLoading"></Loading>

    <table class="table table-hover" v-show="!state.isLoading">
        <thead>
            <tr>
                <th>{{ $Resources.DomainWhitelistsDomain }}</th>
                <th></th>
            </tr>
        </thead>
        <tbody class="td-align-middle">
            <tr v-for="domainWhitelist in state.domainWhitelists">
                <td>{{ domainWhitelist.Domain }}</td>
                <td class="text-right">
                    <delete-button @delete="removeDomainWhitelist(domainWhitelist)" :disabled="state.isLoading"></delete-button>
                </td>
            </tr>
        </tbody>
    </table>

    <Modal size="sm" :title="$Resources.CreateDomainWhitelist" :show="state.showCreateModal" @close="(val) => (state.showCreateModal = false)">
        <template #content>
            <BSFormInputText
                class="form-group"
                :modelValue="state.domain"
                @update:modelValue="(newValue: string) => (state.domain = newValue)"
                :required="true"
                :label="$Resources.DomainWhitelistsDomain"
                placeholder="https://www.ungapped.com"></BSFormInputText>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" @click="create()" :disabled="state.isLoading || state.domain == ''">
                {{ $Resources.CreateDomainWhitelist }}
            </button>
        </template>
    </Modal>
</template>

<style></style>
